// askSmart-ui/src/pages/TOSP/portal/useProfileComplete.ts
import { useEffect, useState } from "react";

/**
 * Whether this user has a profile template assigned AND has submitted it.
 *
 * Deliberately a copy of the check SMRT Docs makes, not a shared hook. The two
 * modules are entitled separately — an org can hold SMRT Docs and never touch
 * TOSP — so neither should acquire a dependency on the other, and pulling one
 * out later should not require untangling the other.
 *
 * The admin owns both halves: they build the template in Manage Profiles and
 * assign it. Nothing here creates or infers a profile.
 *
 * Returns `null` while the answer is still being fetched. Callers must treat
 * that as neither pass nor fail — reading it as "not complete" flashes the gate
 * at everyone on every load.
 */
export function useProfileComplete(userId: string | undefined): boolean | null {
  const [complete, setComplete] = useState<boolean | null>(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    (async () => {
      const base = process.env.REACT_APP_BACKEND_URL;
      try {
        const assignmentsRes = await fetch(`${base}/user-profile-assignments`, {
          credentials: "include",
        });
        if (!assignmentsRes.ok) throw new Error(`assignments → ${assignmentsRes.status}`);
        const assignments = await assignmentsRes.json();

        const assignment = (Array.isArray(assignments) ? assignments : []).find(
          (row: any) => row.userId === userId || row.user_id === userId,
        );

        // Nothing assigned yet. Gated, but there is nothing the user can do
        // about it — the message has to point at the admin, not at the wizard.
        if (!assignment?.profileTemplateId) {
          if (!cancelled) setComplete(false);
          return;
        }

        const savedRes = await fetch(
          `${base}/profile-submissions/${userId}/${assignment.profileTemplateId}`,
          { credentials: "include" },
        );
        if (!savedRes.ok) throw new Error(`submission → ${savedRes.status}`);
        const saved = await savedRes.json();

        // "submitted" and nothing else. A part-filled draft is not a profile.
        if (!cancelled) setComplete(saved?.status === "submitted");
      } catch (err) {
        // Fails closed. A profile that cannot be verified is not a profile that
        // has been completed, and this gate stands in front of a tenant's
        // trading figures.
        console.error("[TOSP] couldn't check profile status:", err);
        if (!cancelled) setComplete(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return complete;
}
